import palette from 'constants/palette'
import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useStyletron } from 'styletron-react'

// A rect subset — the caller computes it from getBoundingClientRect and only the
// box matters to the spotlight.
export type DOMRectLike = Pick<DOMRect, 'top' | 'left' | 'width' | 'height'>

// Breathing room around the highlighted control so it isn't flush to the cut-out.
export const SPOTLIGHT_PADDING_PX = 8
const RING_WIDTH_PX = 2
const RING_RADIUS_PX = 8
// A box-shadow spread large enough to blanket any viewport around the cut-out.
const SCRIM_SPREAD_PX = 9999
// Dim, don't hide — the page stays visible behind the scrim.
const SCRIM_OPACITY = 0.6
// One below EditNavbar's z-index (1000); the tooltip card (uikit Popover) layers
// above via its own portal. Mirrors Cms EditingOverlay's OVERLAY_Z_INDEX = 999.
// Exported so the wizard sticky-bar ordering invariant test can assert the bar
// stays below the scrim (AI-1123).
export const TOUR_SCRIM_Z_INDEX = 998

interface TourSpotlightProps {
  targetRect: DOMRectLike | null
  // Modal steps (ENG-974's "Nice work" notice) have no cut-out: dim the WHOLE
  // viewport behind the centered card — the standard modal overlay — instead of
  // rendering nothing. Without a rect and without this flag the spotlight stays
  // absent (the pre-measure frame / vanished-anchor fallback).
  dimWholeViewport?: boolean
  // AI-1116: whether the current step lets the highlighted control stay
  // clickable through the scrim (tour2's chatInput step is the only one).
  // Defaults to false — every other step gets its click blocked (by DOM
  // containment against the real anchor node) so the merchant can't click the
  // control while the tour has it spotlit. See the `interactive` doc on
  // TourStep (types.ts) for the full scope of what this does and doesn't block.
  interactive?: boolean
  // The step's `data-tour` anchor id (TourStep.anchorId), passed straight
  // through by the caller (WizardTour) — null/undefined for a centered/modal
  // step, which has no anchor to block. See the blocker effect below for why
  // this replaced a cached targetRect-derived coordinate box (AI-1116 round-5).
  anchorId?: string | null
}

// Portal scrim + spotlight cut-out for the AI-162 wizard tour. Dims the page and
// leaves a highlighted rectangle around the active control. Purely decorative
// (aria-hidden) — the announced step content is the TourTooltipCard (U3).
// NOT a React.FC (project convention); returns null before the first measurement.
//
// Reduced motion: the spotlight is rendered statically — it snaps to each step's
// rect with NO transition/animation — so there is nothing for
// prefers-reduced-motion to suppress. If a move/fade transition is ever added,
// gate it on `(prefers-reduced-motion: reduce)`.
//
// AI-1116 (non-interactive steps): see the `interactive` doc on TourStep
// (types.ts) for what the click blocker below does and doesn't cover.
export const TourSpotlight = ({
  targetRect,
  dimWholeViewport = false,
  interactive = false,
  anchorId,
}: TourSpotlightProps): JSX.Element | null => {
  const [css] = useStyletron()

  // AI-1116 round-3: a pointerEvents:'auto' blocker <div> sitting over the
  // cut-out also became the target for WHEEL events there — wheel's
  // scroll-chaining walks the OVERLAY's own DOM ancestors (this portal → body),
  // not whatever real scrollable element the covered control sits inside (e.g.
  // ChatPanel's transcript), silently breaking scroll on a spotlit panel. A
  // capture-phase document click listener blocks the click without ever
  // becoming a wheel target: the real DOM underneath keeps normal
  // pointer-events (including wheel), and this listener intercepts the click
  // before it reaches it. Keyboard activation is blocked too, not exempt:
  // Enter/Space on a focused native control dispatches a real `click`
  // MouseEvent targeting that element, and a `click` event carries no record
  // of whether it originated from a pointer or a key — so the same
  // DOM-containment check blocks it identically. A `mousedown` listener is
  // registered alongside `click` for the same reason: native focus is a
  // mousedown default action, and several non-interactive anchors wrap real
  // form controls (e.g. TOUR_ANCHORS.simulator's ChatInput,
  // TOUR_ANCHORS.agentSettings's settings form) that would otherwise take
  // focus (and accept typing) on mousedown before the click blocker ever runs.
  //
  // AI-1116 round-5: a coordinate-only check has no concept of z-order or DOM
  // identity. A padded box copied from a stale/zero-rect `targetRect` (e.g. an
  // anchor that hasn't laid out yet — the default `getBoundingClientRect()` in
  // jsdom, but also reachable in production for a `display:none` anchor) sits
  // at the viewport's top-left corner and silently swallows clicks on ANY
  // unrelated element that happens to render there, and any element that
  // visually overlaps the spotlight rect (a portaled dropdown, a toast, the
  // tour's own tooltip card) was likewise unclickable regardless of z-order,
  // patched only ad hoc via the `data-tour-chrome` marker. Blocking by DOM
  // CONTAINMENT against the real anchor node fixes both: re-querying the
  // anchor on every event (rather than caching it) means this can never go
  // stale between remounts (e.g. a persona switch tearing down and rebuilding
  // the anchor under a running tour), and `anchor.contains(event.target)` is
  // z-order-correct by construction — anything rendered on top of the anchor
  // but not inside its DOM subtree (the tooltip card included) is never a
  // descendant, so it's never blocked without needing an allowlist.
  useEffect(() => {
    if (interactive || !anchorId) return undefined

    // stopPropagation so nothing further up the real page's DOM (an onClick
    // handler on the anchor or an ancestor) ever sees it either.
    const blockIfInsideAnchor = (event: MouseEvent) => {
      const anchor = document.querySelector(`[data-tour="${anchorId}"]`)
      if (!anchor || !anchor.contains(event.target as Node)) return
      event.preventDefault()
      event.stopPropagation()
    }

    document.addEventListener('mousedown', blockIfInsideAnchor, true)
    document.addEventListener('click', blockIfInsideAnchor, true)
    return () => {
      document.removeEventListener('mousedown', blockIfInsideAnchor, true)
      document.removeEventListener('click', blockIfInsideAnchor, true)
    }
  }, [interactive, anchorId])

  const fullScreenScrim = css({
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: `${TOUR_SCRIM_Z_INDEX}`,
  })

  if (!targetRect) {
    if (!dimWholeViewport) return null

    // Modal step: one flat scrim over the whole viewport, no cut-out and no ring.
    return createPortal(
      <div aria-hidden="true" data-testid="tour-spotlight">
        <div
          data-testid="tour-spotlight-scrim"
          className={fullScreenScrim}
          style={{
            backgroundColor: palette.grey90,
            opacity: SCRIM_OPACITY,
          }}
        />
      </div>,
      document.body
    )
  }

  const top = targetRect.top - SPOTLIGHT_PADDING_PX
  const left = targetRect.left - SPOTLIGHT_PADDING_PX
  const width = targetRect.width + SPOTLIGHT_PADDING_PX * 2
  const height = targetRect.height + SPOTLIGHT_PADDING_PX * 2

  // Static structural styling stays in css({}). The per-frame geometry (top/left/
  // width/height) AND its palette-token colors are applied inline: the rect comes
  // from a live getBoundingClientRect and cannot be a static css({}) — the
  // CLAUDE.md-sanctioned inline-style exemption for a dynamically-computed rect.
  // The colors are palette tokens (palette.grey90 / palette.belizeHole), never
  // hardcoded hex.
  const layer = css({
    position: 'fixed',
    borderRadius: `${RING_RADIUS_PX}px`,
    // Decorative overlay: never intercept clicks on the highlighted control.
    pointerEvents: 'none',
    zIndex: `${TOUR_SCRIM_Z_INDEX}`,
  })
  const rectStyle = { top, left, width, height }

  return createPortal(
    <div aria-hidden="true" data-testid="tour-spotlight">
      {/* Cut-out: a transparent rect whose huge box-shadow (in the scrim token)
          dims everything around it; `opacity` turns the shadow into a scrim. */}
      <div
        data-testid="tour-spotlight-cutout"
        className={layer}
        style={{
          ...rectStyle,
          boxShadow: `0 0 0 ${SCRIM_SPREAD_PX}px ${palette.grey90}`,
          opacity: SCRIM_OPACITY,
        }}
      />
      {/* Focus-ring style highlight framing the control. */}
      <div
        data-testid="tour-spotlight-ring"
        className={layer}
        style={{
          ...rectStyle,
          border: `${RING_WIDTH_PX}px solid ${palette.belizeHole}`,
        }}
      />
    </div>,
    document.body
  )
}
